import type { ReactElement } from 'react';

import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { GridFilterItem } from './GridFilterItem';
import {
  Option,
  OptionItemBtn,
  OptionItemWrapper,
  OptionItemTitle,
  OptionItemAppliedFilters,
  Details,
  DetailsList,
  DetailsListItem,
  SizeGroupList,
  SizeGroupListItem,
  ShowMoreLessBtn,
  AppliedColor,
  ScreenReaderOnly,
} from './OptionGroupStyles';
import { groupSizeItems, SIZE_GROUP_ORDER } from './sizeUtils';
import { StandardFilterItem } from './StandardFilterItem';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';

export type OptionItem = {
  key: string;
  label: string;
  selected: boolean;
  bgColor?: string;
  testId?: string;
};

type OptionGroupProps = {
  title: string;
  valueEN: string;
  summary?: string;
  items: OptionItem[];
  onSelect: (key: string) => void;
  defaultExpanded?: boolean;
  getIconColor?: (bg?: string) => string | undefined;
  listClassName?: string;
  checkboxType?: string;
  collapseAt?: number;
  isExpanded?: boolean;
  onToggle?: () => void;
};

export const OptionGroup = ({
  title,
  valueEN,
  summary,
  items,
  onSelect,
  defaultExpanded = false,
  getIconColor,
  listClassName,
  checkboxType,
  collapseAt,
  isExpanded: externalIsExpanded,
  onToggle,
}: OptionGroupProps): ReactElement => {
  const [internalIsExpanded, setInternalIsExpanded] = useState(defaultExpanded);

  // Use external state if provided, otherwise use internal state
  const isExpanded = externalIsExpanded ?? internalIsExpanded;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalIsExpanded(v => !v);
    }
  };
  const [showAll, setShowAll] = useState(false);
  const { systemTexts } = useProductListContext();
  const showMoreLabel = systemTexts?.seo?.showMoreBtnText || '';
  const showLessLabel = systemTexts?.seo?.showLessBtnText || '';

  const isColorFilter = listClassName === 'colorFilter';
  const isGridFilter = listClassName === 'sizeFilter' || listClassName === 'lengthFilter';
  const appliedSummary = !isExpanded ? summary : undefined;
  const selectedColorNames = isColorFilter
    ? items
        .filter(item => item.selected)
        .map(item => item.label)
        .join(', ')
    : undefined;

  const canCollapseList = typeof collapseAt === 'number' && items.length > (collapseAt ?? 0);
  const displayedItems = canCollapseList && !showAll ? items.slice(0, collapseAt) : items;
  const remaining = canCollapseList ? items.length - Number(collapseAt) : 0;

  return (
    <Option>
      <OptionItemBtn
        type="button"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        data-testid={valueEN === 'sort' ? 'sort-options' : `filter-${valueEN}`}
      >
        <OptionItemWrapper>
          <OptionItemTitle>{title}</OptionItemTitle>
          {appliedSummary && (
            <OptionItemAppliedFilters>
              {isColorFilter
                ? appliedSummary
                    .split(',')
                    .map(item => (
                      <AppliedColor
                        key={item}
                        aria-hidden="true"
                        $bgColor={item || 'transparent'}
                      />
                    ))
                : appliedSummary.replaceAll(',', ', ')}
              {selectedColorNames && <ScreenReaderOnly>{selectedColorNames}</ScreenReaderOnly>}
            </OptionItemAppliedFilters>
          )}
        </OptionItemWrapper>
        {isExpanded ? (
          <CaretUpIcon weight="bold" aria-hidden="true" />
        ) : (
          <CaretDownIcon weight="bold" aria-hidden="true" />
        )}
      </OptionItemBtn>

      <Details $display={isExpanded} aria-hidden={!isExpanded}>
        <DetailsList
          className={listClassName}
          role={checkboxType === 'round' ? 'radiogroup' : undefined}
          aria-label={checkboxType === 'round' ? title : undefined}
        >
          {isGridFilter
            ? (() => {
                const groups = groupSizeItems(displayedItems);

                return SIZE_GROUP_ORDER.map(groupName => {
                  const items = groups[groupName];

                  if (items.length === 0) {
                    return null;
                  }

                  return (
                    <SizeGroupListItem key={`group-${groupName}`}>
                      <SizeGroupList className={`size-group size-group-${groupName}`}>
                        {items.map((item: OptionItem) => (
                          <GridFilterItem
                            key={item.key}
                            item={item}
                            onSelect={onSelect}
                            tabIndex={isExpanded ? 0 : -1}
                          />
                        ))}
                      </SizeGroupList>
                    </SizeGroupListItem>
                  );
                }).filter(Boolean);
              })()
            : displayedItems.map(it => (
                <StandardFilterItem
                  key={it.key}
                  item={it}
                  onSelect={onSelect}
                  checkboxType={checkboxType}
                  getIconColor={getIconColor}
                  tabIndex={isExpanded ? 0 : -1}
                />
              ))}
          {canCollapseList && (
            <DetailsListItem>
              {(showMoreLabel || showLessLabel) && (
                <ShowMoreLessBtn
                  type="button"
                  onClick={() => setShowAll(v => !v)}
                  tabIndex={isExpanded ? 0 : -1}
                >
                  {showAll ? showLessLabel : showMoreLabel.replace('{%D}', String(remaining))}
                </ShowMoreLessBtn>
              )}
            </DetailsListItem>
          )}
        </DetailsList>
      </Details>
    </Option>
  );
};
